import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Box,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Paper,
  Divider,
  IconButton,
  TextField,
  Popover,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import NavigateNext from "@mui/icons-material/NavigateNext";
import SettingsIcon from "@mui/icons-material/Settings";
import dayjs from "dayjs";

import TimetableLane from "./lanes/TimetableLane";
import TvNumberStepper from "./TvNumberStepper";
import useResidentialTimetable from "../hooks/useResidentialTimetable";
import useMorningTimetable from "../hooks/useMorningTimetable";
import useMorningClasses from "../hooks/useMorningClasses";
import useActivitiesTimetable from "../hooks/useActivitiesTimetable";
import useTravelTimetable from "../hooks/useTravelTimetable";
import ResidentialLane from "./lanes/ResidentialLane";
import ArrivalsLeaversLane from "./lanes/ArrivalsLeaversLane";
import MorningLane from "./lanes/MorningLane";
import ActivityLane from "./lanes/ActivityLane";
import { getWeeklyStaffScheduling } from "../api/staffSchedulingApi";
import {
  mondayOfWeek,
  excludeStaff,
  groupByHouse,
  groupByClass,
} from "../utils/timetableUtils";

const flagTrue = (v) => v === true || v === 1;
const inRange = (d, start, end) =>
  (!start || d >= String(start).slice(0, 10)) && (!end || d <= String(end).slice(0, 10));

function hasLane(lanes, key) {
  return lanes.some((lane) => lane.key === key);
}

function hasTravelItems(travel) {
  return (
    (travel?.arrivals?.length || 0) > 0 ||
    (travel?.departures?.length || 0) > 0 ||
    (travel?.internalTransfers?.length || 0) > 0
  );
}

export default function TimetableBoard({
  campuses = [],
  campusesLoading = false,
  selectedDate,
  setSelectedDate,
  selectedCampusId,
  setSelectedCampusId,
  lanes = [],
  loading = false,
  error = "",
  onRefresh,
  onOpenBedrooms,
  onOpenTransfers,
  onOpenTransport,
  onOpenClasses,
  onOpenActivities,
  onOpenStaffSchedule,
  kiosk = false,
  onLogout,
}) {
  const selectedCampus = useMemo(
    () => campuses.find((c) => String(c.id) === String(selectedCampusId)) || null,
    [campuses, selectedCampusId]
  );

  const formattedDate = selectedDate?.format("YYYY-MM-DD");
  const campusName = selectedCampus?.name || "";

  // Kiosk accounts boot straight into the TV view and can't drop out of it.
  const [tv, setTv] = useState(Boolean(kiosk));

  // TV layout, adjustable on the fly via the settings popover.
  const [tvColumns, setTvColumns] = useState(3); // columns of people within each card
  const [tvMaxPeople, setTvMaxPeople] = useState(100); // ≈ people that fit one screen
  const [tvMaxGroups, setTvMaxGroups] = useState(6); // max cards (houses/groups/classes) per screen
  const [tvSettingsAnchor, setTvSettingsAnchor] = useState(null);

  // Key staff (camp manager / DSL / first aid) for the TV header — one weekly
  // staff-scheduling call per campus (Monday-anchored, so it's cached within the week).
  const [keyStaff, setKeyStaff] = useState(null);
  useEffect(() => {
    if (!tv || !campusName || !selectedDate) {
      setKeyStaff(null);
      return undefined;
    }
    let cancelled = false;
    const dateStr = selectedDate.format("YYYY-MM-DD");
    const weekStart = mondayOfWeek(selectedDate)?.format("YYYY-MM-DD");
    getWeeklyStaffScheduling(campusName, weekStart)
      .then((resp) => {
        if (cancelled) return;
        const rows = Array.isArray(resp?.rows) ? resp.rows : Array.isArray(resp) ? resp : [];
        const onDate = rows.filter(
          (r) =>
            r.staff === true &&
            Array.isArray(r.scheduledDates) &&
            r.scheduledDates.map((d) => String(d).slice(0, 10)).includes(dateStr) &&
            inRange(dateStr, r.arrivalDate, r.departureDate)
        );
        const names = (pred) =>
          Array.from(new Set(onDate.filter(pred).map((r) => r.name).filter(Boolean)));
        // DSL / First Aid are carried in `qualifications`; Camp Manager in `roles`.
        // Keep the boolean fallbacks in case the backend adds dsl/firstaid flags later.
        const hasIn = (arr, re) => (Array.isArray(arr) ? arr : []).some((x) => re.test(String(x)));
        setKeyStaff({
          campManager: names((r) => hasIn(r.roles, /camp.?manager/i)),
          dsl: names((r) => flagTrue(r.dsl) || hasIn(r.qualifications, /^dsl$/i)),
          firstAid: names((r) => flagTrue(r.firstaid) || hasIn(r.qualifications, /first.?aid/i)),
        });
      })
      .catch(() => {
        if (!cancelled) setKeyStaff(null);
      });
    return () => {
      cancelled = true;
    };
  }, [tv, campusName, selectedDate]);

  const needsAfternoon = useMemo(() => hasLane(lanes, "AFTERNOON"), [lanes]);
  const needsEvening = useMemo(() => hasLane(lanes, "EVENING"), [lanes]);
  const needsDay = useMemo(() => hasLane(lanes, "DAY"), [lanes]);

  const {
    data: residents = [],
    loading: residentialLoading,
    error: residentialError,
    refresh: refreshResidential,
  } = useResidentialTimetable(formattedDate, campusName);

  const {
    data: morningResidents = [],
    loading: morningLoading,
    error: morningError,
    refresh: refreshMorning,
  } = useMorningTimetable(formattedDate, campusName);

  // All classes for the day (including empty ones) so the morning lane can show
  // classes nobody is assigned to yet, not just the ones people group into.
  const { classes: morningClasses = [], refresh: refreshMorningClasses } =
    useMorningClasses(formattedDate, campusName);

  const {
    data: afternoonActivities = { unassigned: [], groups: [] },
    loading: afternoonLoading,
    error: afternoonError,
    refresh: refreshAfternoon,
  } = useActivitiesTimetable(formattedDate, campusName, "AFTERNOON", {
    enabled: needsAfternoon,
  });

  const {
    data: eveningActivities = { unassigned: [], groups: [] },
    loading: eveningLoading,
    error: eveningError,
    refresh: refreshEvening,
  } = useActivitiesTimetable(formattedDate, campusName, "EVENING", {
    enabled: needsEvening,
  });

  const {
    data: dayActivities = { unassigned: [], groups: [] },
    loading: dayLoading,
    error: dayError,
    refresh: refreshDay,
  } = useActivitiesTimetable(formattedDate, campusName, "DAY", {
    enabled: needsDay,
  });

  const {
    data: travel = { arrivals: [], departures: [], internalTransfers: [] },
    loading: travelLoading,
    error: travelError,
    refresh: refreshTravel,
  } = useTravelTimetable(formattedDate, campusName);

  const handleRefresh = async () => {
    await Promise.allSettled([
      onRefresh?.(),
      refreshResidential?.(),
      refreshMorning?.(),
      refreshMorningClasses?.(),
      refreshAfternoon?.(),
      refreshEvening?.(),
      refreshDay?.(),
      refreshTravel?.(),
    ]);
  };

  const handlePrevDay = () => {
    setSelectedDate(selectedDate.subtract(1, "day"));
  };

  const handleNextDay = () => {
    setSelectedDate(selectedDate.add(1, "day"));
  };

  const handleDateChange = (event) => {
    const newDate = dayjs(event.target.value);
    if (newDate.isValid()) {
      setSelectedDate(newDate);
    }
  };

  const hasArrivalsLeavers = useMemo(() => hasTravelItems(travel), [travel]);

  const boardLoading =
    loading ||
    residentialLoading ||
    morningLoading ||
    afternoonLoading ||
    eveningLoading ||
    dayLoading ||
    travelLoading;

  const boardError =
    error ||
    residentialError ||
    morningError ||
    afternoonError ||
    eveningError ||
    dayError ||
    travelError;

  const renderLane = (lane) => {
    if (lane.key === "RESIDENTIAL") {
      return (
        <>
          <ResidentialLane
            residents={residents}
            selectedDate={formattedDate}
            selectedCampusId={selectedCampusId}
            onOpenBedrooms={onOpenBedrooms}
            onOpenTransfers={onOpenTransfers}
            onOpenTransport={onOpenTransport}
            onOpenStaffSchedule={onOpenStaffSchedule}
            tv={tv}
          />

          {hasArrivalsLeavers ? (
            <>
              <Divider />
              <ArrivalsLeaversLane
                travel={travel}
                selectedDate={formattedDate}
                selectedCampusId={selectedCampusId}
                onOpenBedrooms={onOpenBedrooms}
                onOpenTransfers={onOpenTransfers}
                onOpenTransport={onOpenTransport}
                tv={tv}
              />
            </>
          ) : null}
        </>
      );
    }

    if (lane.key === "MORNING") {
      return (
        <MorningLane
          residents={morningResidents}
          classList={morningClasses}
          selectedDate={formattedDate}
          selectedCampusId={selectedCampusId}
          onOpenClasses={onOpenClasses}
          tv={tv}
        />
      );
    }

    if (lane.key === "AFTERNOON") {
      return (
        <ActivityLane
          timetable={afternoonActivities}
          slot="AFTERNOON"
          selectedDate={formattedDate}
          selectedCampusId={selectedCampusId}
          onOpenActivities={onOpenActivities}
          onOpenTransport={onOpenTransport}
          tv={tv}
        />
      );
    }

    if (lane.key === "EVENING") {
      return (
        <ActivityLane
          timetable={eveningActivities}
          slot="EVENING"
          selectedDate={formattedDate}
          selectedCampusId={selectedCampusId}
          onOpenActivities={onOpenActivities}
          onOpenTransport={onOpenTransport}
          tv={tv}
        />
      );
    }

    if (lane.key === "DAY") {
      return (
        <ActivityLane
          timetable={dayActivities}
          slot="DAY"
          selectedDate={formattedDate}
          selectedCampusId={selectedCampusId}
          onOpenActivities={onOpenActivities}
          onOpenTransport={onOpenTransport}
          tv={tv}
        />
      );
    }

    return <TimetableLane title={lane.title} items={lane.items} />;
  };

  const boardContent = boardLoading ? (
    <Box sx={{ py: 6, textAlign: "center" }}>
      <CircularProgress />
    </Box>
  ) : boardError ? (
    <Paper sx={{ p: 2 }}>
      <Typography color="error">
        {typeof boardError === "string"
          ? boardError
          : boardError?.message || "Failed to load timetable"}
      </Typography>
    </Paper>
  ) : (
    <Stack spacing={2}>
      {lanes.map((lane) => (
        <React.Fragment key={lane.key}>
          {renderLane(lane)}
          <Divider />
        </React.Fragment>
      ))}
    </Stack>
  );

  // TV-view sections, each with a `weight` (≈ people on it). Short sections are then
  // packed together so a single screen can carry a few small lanes; a busy lane gets a
  // screen to itself. Each rendered lane shows its own heading.
  const tvPages = useMemo(() => {
    const sections = [];
    const activityWeight = (t) =>
      (t?.unassigned?.length || 0) +
      (t?.groups || []).reduce((s, g) => s + (g.members?.length || 0), 0);
    // Card counts mirror what each lane actually renders in TV mode (staff excluded).
    const activityCards = (t) =>
      (t?.groups?.length || 0) + (excludeStaff(t?.unassigned || []).length > 0 ? 1 : 0);
    const isResidentialPerson = (p) => p?.residential !== false;
    const travelCards = () => {
      const arr = excludeStaff(travel?.arrivals || []);
      const dep = excludeStaff(travel?.departures || []);
      const groups = [
        arr.filter(isResidentialPerson), // Arriving Today
        dep.filter(isResidentialPerson), // Leaving Today
        excludeStaff(travel?.internalTransfers || []), // Internal Transfers
        [...arr, ...dep].filter((p) => !isResidentialPerson(p) && p?.personType !== "ADULT"), // Day Students
        [...arr, ...dep].filter((p) => !isResidentialPerson(p) && p?.personType === "ADULT"), // Day Staff
      ];
      return groups.filter((g) => g.length > 0).length;
    };

    if (hasLane(lanes, "RESIDENTIAL")) {
      sections.push({
        key: "residents",
        weight: residents.length,
        cards: Object.keys(groupByHouse(excludeStaff(residents))).length,
        render: (range) => (
          <ResidentialLane
            residents={residents}
            selectedDate={formattedDate}
            selectedCampusId={selectedCampusId}
            tv
            tvColumns={tvColumns}
            tvCardRange={range}
          />
        ),
      });
      if (hasArrivalsLeavers) {
        sections.push({
          key: "travel",
          weight:
            (travel?.arrivals?.length || 0) +
            (travel?.departures?.length || 0) +
            (travel?.internalTransfers?.length || 0),
          cards: travelCards(),
          render: (range) => (
            <ArrivalsLeaversLane
              travel={travel}
              selectedDate={formattedDate}
              selectedCampusId={selectedCampusId}
              tv
              tvColumns={tvColumns}
              tvCardRange={range}
            />
          ),
        });
      }
    }
    if (hasLane(lanes, "MORNING")) {
      sections.push({
        key: "morning",
        weight: morningResidents.length,
        // Card count = classes with people UNION empty classes from the list.
        cards: (() => {
          const norm = (s) => String(s || "").trim().toLowerCase();
          const names = new Set(
            Object.keys(
              groupByClass(morningResidents.filter((p) => p?.personType !== "ADULT"))
            ).map(norm)
          );
          (morningClasses || []).forEach((c) => {
            const n = norm(c?.groupName);
            if (n && n !== "unassigned") names.add(n);
          });
          return names.size;
        })(),
        render: (range) => (
          <MorningLane
            residents={morningResidents}
            classList={morningClasses}
            selectedDate={formattedDate}
            selectedCampusId={selectedCampusId}
            tv
            tvColumns={tvColumns}
            tvCardRange={range}
          />
        ),
      });
    }
    const activitySection = (slot, timetable) => ({
      key: slot.toLowerCase(),
      weight: activityWeight(timetable),
      cards: activityCards(timetable),
      render: (range) => (
        <ActivityLane
          timetable={timetable}
          slot={slot}
          selectedDate={formattedDate}
          selectedCampusId={selectedCampusId}
          tv
          tvColumns={tvColumns}
          tvCardRange={range}
        />
      ),
    });
    if (hasLane(lanes, "AFTERNOON")) sections.push(activitySection("AFTERNOON", afternoonActivities));
    if (hasLane(lanes, "EVENING")) sections.push(activitySection("EVENING", eveningActivities));
    if (hasLane(lanes, "DAY")) sections.push(activitySection("DAY", dayActivities));

    // Expand into "units": a lane that fits the card budget stays whole; a lane
    // with MORE cards than the budget is split into budget-sized chunks (each an
    // atomic unit shown on its own screen). A chunk is never placed beside another
    // lane — we only ever split a lane that's too big to fit on one screen.
    const maxCards = Math.max(1, tvMaxGroups);
    const units = [];
    sections.forEach((s) => {
      const n = s.cards || 0;
      if (n > maxCards) {
        for (let start = 0; start < n; start += maxCards) {
          const end = Math.min(start + maxCards, n);
          units.push({
            key: `${s.key}-${start}`,
            cards: end - start,
            weight: 0,
            atomic: true,
            render: () => s.render([start, end]),
          });
        }
      } else {
        units.push({
          key: s.key,
          cards: n,
          weight: s.weight,
          atomic: false,
          render: () => s.render(null),
        });
      }
    });

    // Pack whole (non-atomic) lanes together within both budgets; atomic chunks
    // always get a screen to themselves.
    const pages = [];
    let cur = [];
    let curCards = 0;
    let curPeople = 0;
    const flush = () => {
      if (cur.length) pages.push(cur);
      cur = [];
      curCards = 0;
      curPeople = 0;
    };
    units.forEach((u) => {
      if (u.atomic) {
        flush();
        pages.push([u]);
        return;
      }
      if (cur.length && (curCards + u.cards > maxCards || curPeople + u.weight > tvMaxPeople)) {
        flush();
      }
      cur.push(u);
      curCards += u.cards;
      curPeople += u.weight;
    });
    flush();
    return pages;
  }, [
    lanes,
    hasArrivalsLeavers,
    residents,
    travel,
    morningResidents,
    morningClasses,
    afternoonActivities,
    eveningActivities,
    dayActivities,
    formattedDate,
    selectedCampusId,
    tvColumns,
    tvMaxPeople,
    tvMaxGroups,
  ]);

  const [tvIndex, setTvIndex] = useState(0);
  const screensLenRef = useRef(0);
  screensLenRef.current = tvPages.length;
  const refreshRef = useRef(null);
  refreshRef.current = handleRefresh;

  // Reset to the first section when (re)entering TV view.
  useEffect(() => {
    if (tv) setTvIndex(0);
  }, [tv]);

  // Auto-advance every 20s while in TV view; refresh the data each time it wraps to the start.
  useEffect(() => {
    if (!tv) return undefined;
    const id = setInterval(() => {
      setTvIndex((i) => {
        const len = screensLenRef.current || 1;
        const next = (i + 1) % len;
        if (next === 0) refreshRef.current?.();
        return next;
      });
    }, 20000);
    return () => clearInterval(id);
  }, [tv]);

  const tvPrev = () => setTvIndex((i) => (i - 1 + (tvPages.length || 1)) % (tvPages.length || 1));
  const tvNext = () => setTvIndex((i) => (i + 1) % (tvPages.length || 1));

  // TV view: a fullscreen, chrome-free display that cycles through one section at a time
  // (Residents → Arrivals & Departures → Morning → Afternoon → Evening), auto-advancing
  // and refreshing the data each time it wraps round.
  if (tv) {
    const page = tvPages[Math.min(tvIndex, Math.max(tvPages.length - 1, 0))] || null;
    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: (theme) => theme.zIndex.modal + 1,
          bgcolor: "background.paper",
          overflow: "auto",
          p: 3,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h5">
            {campusName || "All campuses"} · {selectedDate?.format("ddd D MMM YYYY")}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={(e) => setTvSettingsAnchor(e.currentTarget)}
              aria-label="TV layout settings"
            >
              <SettingsIcon />
            </IconButton>
            <IconButton onClick={tvPrev} aria-label="Previous section">
              <NavigateBefore />
            </IconButton>
            <IconButton onClick={tvNext} aria-label="Next section">
              <NavigateNext />
            </IconButton>
            {kiosk ? (
              // Locked display: no way back to the editable board — only log out
              // (to switch the device to another account).
              onLogout && (
                <Button variant="contained" color="inherit" onClick={onLogout}>
                  Log out
                </Button>
              )
            ) : (
              <Button variant="contained" onClick={() => setTv(false)}>
                Close TV view
              </Button>
            )}
          </Stack>
        </Stack>

        <Popover
          open={Boolean(tvSettingsAnchor)}
          anchorEl={tvSettingsAnchor}
          onClose={() => setTvSettingsAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
        >
          <Stack spacing={2} sx={{ p: 2, width: 260 }}>
            <Typography variant="subtitle2">TV layout</Typography>
            <TvNumberStepper
              label="Columns per card"
              value={tvColumns}
              onChange={setTvColumns}
              min={1}
              max={6}
              helperText="People columns within each card (1–6)"
            />
            <TvNumberStepper
              label="Max people per screen"
              value={tvMaxPeople}
              onChange={setTvMaxPeople}
              min={1}
              max={200}
              step={5}
              helperText="Short lanes pack together up to this many people"
            />
            <TvNumberStepper
              label="Max cards per screen"
              value={tvMaxGroups}
              onChange={setTvMaxGroups}
              min={1}
              max={20}
              helperText="Total cards (houses/groups/classes) per screen"
            />
          </Stack>
        </Popover>

        <Stack direction="row" justifyContent="center" spacing={0.75} sx={{ mb: 2 }}>
          {tvPages.map((p, i) => (
            <Box
              key={p.map((s) => s.key).join("-")}
              onClick={() => setTvIndex(i)}
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                cursor: "pointer",
                bgcolor: i === tvIndex ? "primary.main" : "action.disabled",
              }}
            />
          ))}
        </Stack>

        {campusName ? (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Stack
              direction="row"
              spacing={3}
              flexWrap="wrap"
              rowGap={0.5}
              justifyContent="center"
            >
              <Typography variant="body2">
                <strong>Camp Manager:</strong> {keyStaff?.campManager?.join(", ") || "—"}
              </Typography>
              <Typography variant="body2">
                <strong>DSL:</strong> {keyStaff?.dsl?.join(", ") || "—"}
              </Typography>
              <Typography variant="body2">
                <strong>First Aid:</strong> {keyStaff?.firstAid?.join(", ") || "—"}
              </Typography>
            </Stack>
          </Paper>
        ) : null}

        {boardLoading ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : boardError ? (
          <Paper sx={{ p: 2 }}>
            <Typography color="error">
              {typeof boardError === "string" ? boardError : boardError?.message || "Failed to load timetable"}
            </Typography>
          </Paper>
        ) : page && page.length ? (
          page.map((section, i) => (
            <React.Fragment key={section.key}>
              {i > 0 ? <Divider sx={{ my: 3 }} /> : null}
              {section.render()}
            </React.Fragment>
          ))
        ) : (
          <Typography sx={{ opacity: 0.7 }}>Nothing scheduled to show.</Typography>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={handlePrevDay}>
            <NavigateBefore />
          </IconButton>

          <TextField
            type="date"
            size="small"
            value={formattedDate || ""}
            onChange={handleDateChange}
          />

          <IconButton onClick={handleNextDay}>
            <NavigateNext />
          </IconButton>
        </Stack>

        <FormControl size="small" sx={{ minWidth: 220 }} disabled={campusesLoading}>
          <InputLabel id="timetable-campus-label">Campus</InputLabel>
          <Select
            labelId="timetable-campus-label"
            value={
              campuses.some((c) => String(c.id) === String(selectedCampusId))
                ? selectedCampusId
                : ""
            }
            label="Campus"
            onChange={(e) => setSelectedCampusId(e.target.value)}
          >
            <MenuItem value="">
              <em>All campuses</em>
            </MenuItem>

            {campuses.map((campus) => (
              <MenuItem key={campus.id} value={String(campus.id)}>
                {campus.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>

        <Button variant="outlined" onClick={() => setTv(true)}>
          TV view
        </Button>
      </Stack>

      {boardContent}
    </Box>
  );
}